import { useState, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";

import { cn } from "@/lib/utils";

interface CodeBlockProps {
  children: ReactNode;
  language?: string;
  copyValue?: string;
  className?: string;
}

/**
 * Copy-to-clipboard code snippet. ``copyValue`` lets the caller
 * pass a flat string when ``children`` carries formatted markup or
 * newlines that ``innerText`` would munge.
 */
export function CodeBlock({
  children,
  language,
  copyValue,
  className,
}: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const text =
      copyValue ??
      (typeof children === "string" ? children : String(children));
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Permission denied / non-secure context — silently fall
      // through; the user can still select + copy manually.
    }
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-md border border-ocean-100 bg-neutral-50",
        className
      )}
    >
      {language && (
        <div className="border-b border-ocean-100 bg-white px-3 py-1 font-mono text-[10px] uppercase tracking-wide text-ocean-600">
          {language}
        </div>
      )}
      <pre className="overflow-x-auto px-3 py-3 text-xs font-mono text-neutral-800">
        <code>{children}</code>
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Tersalin" : "Salin"}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-ocean-100 bg-white px-2 py-1 text-xs text-ocean-600 transition-colors hover:bg-ocean-50"
      >
        {copied ? (
          <>
            <Check size={12} className="text-success-600" />
            Tersalin
          </>
        ) : (
          <>
            <Copy size={12} />
            Salin
          </>
        )}
      </button>
    </div>
  );
}
